// ============================================================================
// midi.js — MIDI Note Cue Handler
// ----------------------------------------------------------------------------
// Implements midi(...) cue:
//   midi(ch:1, pitch:y, vel:size, dur:0.5, uid:myEl)
//
// • Event-based (snapshot at trigger time)
// • pitch:y maps element Y position to MIDI note range (loNote–hiNote)
// • vel:size maps element size to velocity 1–127
// • dur triggers note-off after N beats
// • Works with propagate() and all trigger modes
// ============================================================================

import {
    needsArming,
    armElement
} from "./animShared.js";
import { secs } from "./beats.js";

// ---------------------------------------------------------------------------
// Visual sampler (shared logic with osc.js)
// ---------------------------------------------------------------------------
function clamp01(v) { return Math.min(1, Math.max(0, v)); }

function sampleVisual(el) {
    if (!el || !el.getBoundingClientRect) return null;

    const vp = (window.scoreContainer || document.body).getBoundingClientRect();
    if (!vp.width || !vp.height) return null;

    const box = el.getBoundingClientRect();
    const cx = box.left + box.width / 2;
    const cy = box.top + box.height / 2;

    const x = clamp01((cx - vp.left) / vp.width);
    const y = clamp01(1 - (cy - vp.top) / vp.height); // invert Y: top = high pitch

    const widthRaw  = clamp01(box.width / vp.width);
    const heightRaw = clamp01(box.height / vp.height);
    const size = Math.max(Math.sqrt(widthRaw), Math.sqrt(heightRaw));

    return { x, y, size };
}

// ---------------------------------------------------------------------------
// WebSocket send
// ---------------------------------------------------------------------------
function sendMidi(payload) {
    if (!window.socket || window.socket.readyState !== WebSocket.OPEN) return;
    try { window.socket.send(JSON.stringify(payload)); }
    catch (e) { console.warn("[midi] send failed:", e); }
}

// ---------------------------------------------------------------------------
// Main handler
// ---------------------------------------------------------------------------
export function handleMidiCue(ast, el, options = {}) {
    if (!ast || !el) return;

    const astArgs = ast.args || [];

    let ch     = 1;       // MIDI channel 1–16
    let loNote = 36;      // pitch:y range low  (C2)
    let hiNote = 84;      // pitch:y range high (C6)
    let fixedNote = null; // explicit note number overrides pitch:y
    let fixedVel  = 80;   // default velocity when vel:size not used
    let gate      = 1;    // gate:0 releases the note instead of starting one
    let velSource = null; // "size" | "x" | "y" etc
    let dur    = null;    // note duration in beats (triggers note-off)

    for (const a of astArgs) {
        const key = a.type;
        const val = a.value;

        if (key === "ch" || key === "channel") {
            const n = Math.round(Number(val));
            if (Number.isFinite(n)) ch = Math.max(1, Math.min(16, n));   // ch:x keeps channel 1
        }
        else if (key === "note") fixedNote = Math.round(Number(val));
        else if (key === "vel") {
            if (typeof val === "string") velSource = val;   // e.g. vel:size
            // floor 0, not 1: vel:0 is how MIDI says "release", and clamped
            // to 1 every intended note-off would be a near-silent note-on
            else fixedVel = Math.max(0, Math.min(127, Math.round(Number(val))));
        }
        else if (key === "pitch") {
            if (typeof val === "string") {
                // pitch:y  or  pitch:y-36-84
                const parts = val.split("-");
                // parts[0] is visual source (ignored — always y for now)
                if (parts[1] != null) loNote = Number(parts[1]);
                if (parts[2] != null) hiNote = Number(parts[2]);
            } else {
                fixedNote = Math.round(Number(val));
            }
        }
        else if (key === "lo") loNote = Number(val);
        else if (key === "hi") hiNote = Number(val);
        else if (key === "dur") dur = Number(val);
        // gate:0 sends a real note-off — the readable way to release a held
        // note, e.g. a "drone off" button paired with a gate:1 button
        else if (key === "gate") gate = Number(val) ? 1 : 0;
    }

    const fire = () => {
        const sample = sampleVisual(el);
        if (!sample) return;

        // Resolve note
        const note = fixedNote != null
            ? fixedNote
            : Math.round(loNote + sample.y * (hiNote - loNote));

        // Resolve velocity
        const vel = velSource
            ? Math.max(1, Math.min(127, Math.round(Number.isFinite(sample[velSource]) ? sample[velSource] * 127 : fixedVel)))
            : fixedVel;

        // gate:0, or an explicit velocity of 0, means release rather than play
        const releasing = gate === 0 || (!velSource && fixedVel === 0);
        const payload = releasing
            ? { type: "midi_note", ch, note, vel: 0, gate: 0 }
            : { type: "midi_note", ch, note, vel, gate: 1 };
        sendMidi(payload);

        const durSecs = secs(dur);   // beats -> seconds, at the tempo of this fire
        if (durSecs != null && durSecs > 0) {
            setTimeout(() => {
                sendMidi({ type: "midi_note", ch, note, vel: 0, gate: 0 });
            }, durSecs * 1000);
        }
    };

    if (needsArming(el)) {
        armElement(el, fire);
        return;
    }

    fire();
}
